import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  FileText, 
  DollarSign, 
  Plus,
  LogOut,
  Home,
  BarChart3,
  Settings,
  Clock,
  CheckCircle,
  AlertCircle,
  Trash2,
  Edit,
  Eye,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Image,
  FolderOpen,
  Star,
  MessageSquare,
  Upload,
  X,
  Save,
  Layers
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const PROJECT_CATEGORIES = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "interior", label: "Interior Build-Outs" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [availability, setAvailability] = useState({ slots: [], blocked_dates: [] });
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, clientsRes, invoicesRes, consultationsRes, availabilityRes, projectsRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/consultations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/availability`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects`),
        axios.get(`${API}/testimonials`),
      ]);
      setStats(statsRes.data);
      setClients(clientsRes.data);
      setInvoices(invoicesRes.data);
      setConsultations(consultationsRes.data);
      setAvailability(availabilityRes.data);
      setProjects(projectsRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin");
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: "bg-green-500/20 text-green-400",
      unpaid: "bg-yellow-500/20 text-yellow-400",
      overdue: "bg-red-500/20 text-red-400",
      cancelled: "bg-gray-500/20 text-gray-400",
    };
    return (
      <span className={`px-2 py-1 text-xs uppercase ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    
    try {
      await axios.delete(`${API}/admin/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const updateConsultationStatus = async (consultationId, newStatus) => {
    try {
      await axios.put(`${API}/admin/consultations/${consultationId}?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Consultation updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update consultation");
    }
  };

  const getConsultationStatusBadge = (status) => {
    const styles = {
      confirmed: "bg-green-500/20 text-green-400",
      pending: "bg-yellow-500/20 text-yellow-400",
      completed: "bg-blue-500/20 text-blue-400",
      cancelled: "bg-red-500/20 text-red-400",
    };
    return (
      <span className={`px-2 py-1 text-xs uppercase ${styles[status]}`}>
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-[#121212] border-r border-[#2A2A2A] flex flex-col">
        <div className="p-6 border-b border-[#2A2A2A]">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_1eeea6a6-945d-4052-ae21-011aad17a3a1/artifacts/qm929xya_fulllogo_transparent_nobuffer.png" 
              alt="Astute Builders" 
              className="h-10"
            />
          </Link>
          <p className="text-[#7ED4C6] text-xs mt-2 uppercase tracking-wider">Admin Dashboard</p>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {[
            { id: "overview", icon: BarChart3, label: "Overview" },
            { id: "content", icon: Layers, label: "Content Editor" },
            { id: "consultations", icon: Calendar, label: "Consultations" },
            { id: "clients", icon: Users, label: "Clients" },
            { id: "invoices", icon: FileText, label: "Invoices" },
            { id: "availability", icon: Clock, label: "Availability" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                activeTab === item.id
                  ? "bg-[#7ED4C6]/10 text-[#7ED4C6] border-l-2 border-[#7ED4C6]"
                  : "text-[#A1A1AA] hover:text-white hover:bg-[#1E1E1E]"
              }`}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-[#2A2A2A]">
          <Link to="/" className="flex items-center gap-3 px-4 py-3 text-[#A1A1AA] hover:text-white">
            <Home size={20} />
            View Website
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-[#A1A1AA] hover:text-red-400"
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div>
            <h1 className="font-['Oswald'] text-3xl text-white mb-8">Dashboard Overview</h1>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Users size={24} className="text-[#7ED4C6]" />
                  <span className="text-[#A1A1AA]">Total Clients</span>
                </div>
                <p className="font-['Oswald'] text-4xl text-white">{stats?.total_clients || 0}</p>
              </div>
              <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                <div className="flex items-center gap-3 mb-2">
                  <FileText size={24} className="text-[#7ED4C6]" />
                  <span className="text-[#A1A1AA]">Total Invoices</span>
                </div>
                <p className="font-['Oswald'] text-4xl text-white">{stats?.total_invoices || 0}</p>
              </div>
              <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign size={24} className="text-green-400" />
                  <span className="text-[#A1A1AA]">Revenue</span>
                </div>
                <p className="font-['Oswald'] text-4xl text-white">
                  ${(stats?.total_revenue || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                <div className="flex items-center gap-3 mb-2">
                  <AlertCircle size={24} className="text-yellow-400" />
                  <span className="text-[#A1A1AA]">Outstanding</span>
                </div>
                <p className="font-['Oswald'] text-4xl text-white">
                  ${(stats?.total_outstanding || 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-6 mb-8">
              <div className="bg-green-500/10 border border-green-500/30 p-6 text-center">
                <CheckCircle size={32} className="text-green-400 mx-auto mb-2" />
                <p className="text-3xl text-white font-['Oswald']">{stats?.paid_invoices || 0}</p>
                <p className="text-[#A1A1AA] text-sm">Paid Invoices</p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-6 text-center">
                <Clock size={32} className="text-yellow-400 mx-auto mb-2" />
                <p className="text-3xl text-white font-['Oswald']">{stats?.unpaid_invoices || 0}</p>
                <p className="text-[#A1A1AA] text-sm">Unpaid Invoices</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 p-6 text-center">
                <AlertCircle size={32} className="text-red-400 mx-auto mb-2" />
                <p className="text-3xl text-white font-['Oswald']">{stats?.overdue_invoices || 0}</p>
                <p className="text-[#A1A1AA] text-sm">Overdue Invoices</p>
              </div>
              <div className="bg-[#7ED4C6]/10 border border-[#7ED4C6]/30 p-6 text-center">
                <Calendar size={32} className="text-[#7ED4C6] mx-auto mb-2" />
                <p className="text-3xl text-white font-['Oswald']">{stats?.confirmed_consultations || 0}</p>
                <p className="text-[#A1A1AA] text-sm">Upcoming Consultations</p>
              </div>
            </div>

            {/* Upcoming Consultations */}
            {stats?.upcoming_consultations?.length > 0 && (
              <div className="bg-[#121212] border border-[#2A2A2A] mb-8">
                <div className="p-6 border-b border-[#2A2A2A] flex justify-between items-center">
                  <h2 className="font-['Oswald'] text-xl text-white">Upcoming Consultations</h2>
                  <button
                    onClick={() => setActiveTab("consultations")}
                    className="text-[#7ED4C6] text-sm hover:underline"
                  >
                    View All →
                  </button>
                </div>
                <div className="divide-y divide-[#2A2A2A]">
                  {stats.upcoming_consultations.slice(0, 3).map((consultation) => (
                    <div key={consultation.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#7ED4C6]/20 flex items-center justify-center">
                          <Calendar size={20} className="text-[#7ED4C6]" />
                        </div>
                        <div>
                          <p className="text-white font-semibold">{consultation.full_name}</p>
                          <p className="text-[#A1A1AA] text-sm">{consultation.project_type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white">{new Date(consultation.date).toLocaleDateString()}</p>
                        <p className="text-[#7ED4C6] font-semibold">{consultation.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Invoices */}
            <div className="bg-[#121212] border border-[#2A2A2A]">
              <div className="p-6 border-b border-[#2A2A2A] flex justify-between items-center">
                <h2 className="font-['Oswald'] text-xl text-white">Recent Invoices</h2>
                <button
                  onClick={() => setActiveTab("invoices")}
                  className="text-[#7ED4C6] text-sm hover:underline"
                >
                  View All →
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#1E1E1E]">
                    <tr>
                      <th className="text-left p-4 text-[#A1A1AA] text-sm">Invoice #</th>
                      <th className="text-left p-4 text-[#A1A1AA] text-sm">Client</th>
                      <th className="text-left p-4 text-[#A1A1AA] text-sm">Amount</th>
                      <th className="text-left p-4 text-[#A1A1AA] text-sm">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.slice(0, 5).map((inv) => (
                      <tr key={inv.id} className="border-t border-[#2A2A2A]">
                        <td className="p-4 text-white font-mono">{inv.invoice_number}</td>
                        <td className="p-4 text-[#A1A1AA]">{inv.client_name}</td>
                        <td className="p-4 text-white">${inv.amount.toLocaleString()}</td>
                        <td className="p-4">{getStatusBadge(inv.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Content Editor Tab */}
        {activeTab === "content" && (
          <ContentEditorTab 
            projects={projects}
            testimonials={testimonials}
            token={token}
            onRefresh={fetchData}
            setShowProjectModal={setShowProjectModal}
            setSelectedProject={setSelectedProject}
            setShowTestimonialModal={setShowTestimonialModal}
            setSelectedTestimonial={setSelectedTestimonial}
          />
        )}

        {/* Clients Tab */}
        {activeTab === "clients" && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h1 className="font-['Oswald'] text-3xl text-white">Clients</h1>
              <button
                onClick={() => setShowClientModal(true)}
                className="btn-primary flex items-center gap-2"
                data-testid="add-client-btn"
              >
                <Plus size={18} />
                Add Client
              </button>
            </div>

            <div className="bg-[#121212] border border-[#2A2A2A]">
              {clients.length === 0 ? (
                <div className="p-12 text-center">
                  <Users size={48} className="text-[#2A2A2A] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No clients yet. Add your first client!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1E1E1E]">
                      <tr>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Name</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Email</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Company</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Phone</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((client) => (
                        <tr key={client.id} className="border-t border-[#2A2A2A] hover:bg-[#1E1E1E]">
                          <td className="p-4 text-white">{client.name}</td>
                          <td className="p-4 text-[#A1A1AA]">{client.email}</td>
                          <td className="p-4 text-[#A1A1AA]">{client.company || "-"}</td>
                          <td className="p-4 text-[#A1A1AA]">{client.phone || "-"}</td>
                          <td className="p-4">
                            <button
                              onClick={() => { setSelectedClient(client); setShowInvoiceModal(true); }}
                              className="text-[#7ED4C6] hover:text-[#9BE0D5] mr-3"
                              title="Create Invoice"
                            >
                              <FileText size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Consultations Tab */}
        {activeTab === "consultations" && (
          <div>
            <h1 className="font-['Oswald'] text-3xl text-white mb-8">Consultations</h1>

            <div className="bg-[#121212] border border-[#2A2A2A]">
              {consultations.length === 0 ? (
                <div className="p-12 text-center">
                  <Calendar size={48} className="text-[#2A2A2A] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No consultations scheduled yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1E1E1E]">
                      <tr>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Date & Time</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Client</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Contact</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Project Type</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Budget</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Status</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consultations.map((consultation) => (
                        <tr key={consultation.id} className="border-t border-[#2A2A2A] hover:bg-[#1E1E1E]">
                          <td className="p-4">
                            <p className="text-white">{new Date(consultation.date).toLocaleDateString()}</p>
                            <p className="text-[#7ED4C6] font-semibold">{consultation.time}</p>
                          </td>
                          <td className="p-4">
                            <p className="text-white">{consultation.full_name}</p>
                            <p className="text-[#A1A1AA] text-sm">{consultation.project_address}</p>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1 text-[#A1A1AA] text-sm">
                              <Phone size={12} /> {consultation.phone}
                            </div>
                            <div className="flex items-center gap-1 text-[#A1A1AA] text-sm">
                              <Mail size={12} /> {consultation.email}
                            </div>
                          </td>
                          <td className="p-4 text-[#A1A1AA]">{consultation.project_type}</td>
                          <td className="p-4 text-[#A1A1AA]">{consultation.budget_range}</td>
                          <td className="p-4">{getConsultationStatusBadge(consultation.status)}</td>
                          <td className="p-4">
                            <select
                              value={consultation.status}
                              onChange={(e) => updateConsultationStatus(consultation.id, e.target.value)}
                              className="bg-[#1E1E1E] text-white text-sm p-1 border border-[#2A2A2A]"
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === "availability" && (
          <div>
            <h1 className="font-['Oswald'] text-3xl text-white mb-8">Availability Settings</h1>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Weekly Schedule */}
              <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                <h2 className="font-['Oswald'] text-xl text-white mb-6">Weekly Schedule</h2>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  Set your availability for each day. Consultations are 30 minutes each.
                </p>
                <div className="space-y-4">
                  {DAYS.map((day, index) => {
                    const slot = availability.slots.find(s => s.day_of_week === index);
                    return (
                      <div key={day} className="flex items-center justify-between p-3 bg-[#1E1E1E]">
                        <span className="text-white">{day}</span>
                        {slot && slot.is_active ? (
                          <span className="text-[#7ED4C6]">{slot.start_time} - {slot.end_time}</span>
                        ) : (
                          <span className="text-[#A1A1AA]">Unavailable</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[#A1A1AA] text-xs mt-4">
                  Default: Mon-Fri 9:00 AM - 5:00 PM. Contact support to modify schedule.
                </p>
              </div>

              {/* Blocked Dates */}
              <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                <h2 className="font-['Oswald'] text-xl text-white mb-6">Blocked Dates</h2>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  These dates are blocked for consultations (holidays, vacations, etc.).
                </p>
                {availability.blocked_dates.length === 0 ? (
                  <p className="text-[#A1A1AA] text-center py-8">No blocked dates</p>
                ) : (
                  <div className="space-y-2">
                    {availability.blocked_dates.map((bd) => (
                      <div key={bd.date || bd} className="flex items-center justify-between p-3 bg-[#1E1E1E]">
                        <span className="text-white">{bd.date || bd}</span>
                        <span className="text-[#A1A1AA] text-sm">{bd.reason || 'Blocked'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h1 className="font-['Oswald'] text-3xl text-white">Invoices</h1>
              <button
                onClick={() => { setSelectedClient(null); setShowInvoiceModal(true); }}
                className="btn-primary flex items-center gap-2"
                data-testid="create-invoice-btn"
              >
                <Plus size={18} />
                Create Invoice
              </button>
            </div>

            <div className="bg-[#121212] border border-[#2A2A2A]">
              {invoices.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText size={48} className="text-[#2A2A2A] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No invoices yet. Create your first invoice!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1E1E1E]">
                      <tr>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Invoice #</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Client</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Description</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Amount</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Due Date</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Status</th>
                        <th className="text-left p-4 text-[#A1A1AA] text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-t border-[#2A2A2A] hover:bg-[#1E1E1E]">
                          <td className="p-4 text-white font-mono">{inv.invoice_number}</td>
                          <td className="p-4 text-[#A1A1AA]">{inv.client_name}</td>
                          <td className="p-4 text-[#A1A1AA] max-w-[200px] truncate">{inv.description}</td>
                          <td className="p-4 text-white font-semibold">${inv.amount.toLocaleString()}</td>
                          <td className="p-4 text-[#A1A1AA]">
                            {new Date(inv.due_date).toLocaleDateString()}
                          </td>
                          <td className="p-4">{getStatusBadge(inv.status)}</td>
                          <td className="p-4">
                            <button
                              onClick={() => deleteInvoice(inv.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Client Modal */}
      {showClientModal && (
        <ClientModal
          onClose={() => setShowClientModal(false)}
          onSuccess={() => { setShowClientModal(false); fetchData(); }}
          token={token}
        />
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <InvoiceModal
          client={selectedClient}
          clients={clients}
          onClose={() => { setShowInvoiceModal(false); setSelectedClient(null); }}
          onSuccess={() => { setShowInvoiceModal(false); setSelectedClient(null); fetchData(); }}
          token={token}
        />
      )}

      {/* Project Modal */}
      {showProjectModal && (
        <ProjectModal
          project={selectedProject}
          onClose={() => { setShowProjectModal(false); setSelectedProject(null); }}
          onSuccess={() => { setShowProjectModal(false); setSelectedProject(null); fetchData(); }}
          token={token}
        />
      )}

      {/* Testimonial Modal */}
      {showTestimonialModal && (
        <TestimonialModal
          testimonial={selectedTestimonial}
          onClose={() => { setShowTestimonialModal(false); setSelectedTestimonial(null); }}
          onSuccess={() => { setShowTestimonialModal(false); setSelectedTestimonial(null); fetchData(); }}
          token={token}
        />
      )}
    </div>
  );
}

// Client Modal Component
function ClientModal({ onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    company: "",
    phone: "",
    address: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/admin/clients`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client created successfully");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#121212] border border-[#2A2A2A] w-full max-w-md">
        <div className="p-6 border-b border-[#2A2A2A] flex justify-between items-center">
          <h2 className="font-['Oswald'] text-xl text-white">Add New Client</h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-white text-sm mb-2">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="form-input w-full"
            />
          </div>
          <div>
            <label className="block text-white text-sm mb-2">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="form-input w-full"
            />
          </div>
          <div>
            <label className="block text-white text-sm mb-2">Password *</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={6}
              className="form-input w-full"
            />
          </div>
          <div>
            <label className="block text-white text-sm mb-2">Company</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="form-input w-full"
            />
          </div>
          <div>
            <label className="block text-white text-sm mb-2">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="form-input w-full"
            />
          </div>
          <button type="submit" disabled={isLoading} className="btn-primary w-full">
            {isLoading ? "Creating..." : "Create Client"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Invoice Modal Component
function InvoiceModal({ client, clients, onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    client_id: client?.id || "",
    description: "",
    amount: "",
    due_date: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/admin/invoices`, {
        ...formData,
        amount: parseFloat(formData.amount),
        due_date: new Date(formData.due_date).toISOString(),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice created successfully");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#121212] border border-[#2A2A2A] w-full max-w-md">
        <div className="p-6 border-b border-[#2A2A2A] flex justify-between items-center">
          <h2 className="font-['Oswald'] text-xl text-white">Create Invoice</h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-white text-sm mb-2">Client *</label>
            <select
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
              required
              className="form-input w-full"
            >
              <option value="">Select a client</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name} - {c.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-white text-sm mb-2">Description *</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              className="form-input w-full"
              placeholder="e.g., Kitchen Renovation - Phase 1"
            />
          </div>
          <div>
            <label className="block text-white text-sm mb-2">Amount ($) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
              className="form-input w-full"
              placeholder="5000.00"
            />
          </div>
          <div>
            <label className="block text-white text-sm mb-2">Due Date *</label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              required
              className="form-input w-full"
            />
          </div>
          <button type="submit" disabled={isLoading} className="btn-primary w-full">
            {isLoading ? "Creating..." : "Create Invoice"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Content Editor Tab Component
function ContentEditorTab({ projects, testimonials, token, onRefresh, setShowProjectModal, setSelectedProject, setShowTestimonialModal, setSelectedTestimonial }) {
  const [contentTab, setContentTab] = useState("pages");
  const [siteContent, setSiteContent] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [activePage, setActivePage] = useState("home");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSiteContent();
  }, []);

  const fetchSiteContent = async () => {
    try {
      // First seed the content if not exists
      await axios.post(`${API}/seed-content`);
      const response = await axios.get(`${API}/site-content`);
      setSiteContent(response.data.content || {});
    } catch (error) {
      console.error("Error fetching site content:", error);
    } finally {
      setIsLoadingContent(false);
    }
  };

  const updatePageContent = (page, section, key, value) => {
    setSiteContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [section]: typeof prev[page]?.[section] === 'object' && !Array.isArray(prev[page]?.[section])
          ? { ...prev[page][section], [key]: value }
          : value
      }
    }));
  };

  const savePageContent = async (page) => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/site-content/${page}`, siteContent[page], {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${page.charAt(0).toUpperCase() + page.slice(1)} page content saved!`);
    } catch (error) {
      toast.error("Failed to save content");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await axios.delete(`${API}/admin/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Project deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const deleteTestimonial = async (testimonialId) => {
    if (!window.confirm("Are you sure you want to delete this testimonial?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${testimonialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Testimonial deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete testimonial");
    }
  };

  return (
    <div data-testid="content-editor-tab">
      <h1 className="font-['Oswald'] text-3xl text-white mb-4">Content Editor</h1>
      <p className="text-[#A1A1AA] mb-8">Manage your website content, projects, and testimonials.</p>

      {/* Content Sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-8 border-b border-[#2A2A2A] pb-4">
        {[
          { id: "pages", icon: Edit, label: "Pages" },
          { id: "services", icon: Settings, label: "Services" },
          { id: "projects", icon: FolderOpen, label: "Projects" },
          { id: "testimonials", icon: MessageSquare, label: "Testimonials" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setContentTab(tab.id)}
            data-testid={`content-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
              contentTab === tab.id
                ? "bg-[#7ED4C6] text-[#0A0A0A]"
                : "bg-[#1E1E1E] text-white hover:bg-[#2A2A2A]"
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Pages Editor */}
      {contentTab === "pages" && (
        <div>
          {isLoadingContent ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto" />
              <p className="text-[#A1A1AA] mt-4">Loading content...</p>
            </div>
          ) : (
            <>
              {/* Page Selector */}
              <div className="flex flex-wrap gap-2 mb-6">
                {["home", "about", "contact", "footer"].map((page) => (
                  <button
                    key={page}
                    onClick={() => setActivePage(page)}
                    className={`px-4 py-2 text-sm capitalize ${
                      activePage === page
                        ? "bg-white text-[#0A0A0A]"
                        : "bg-[#1E1E1E] text-white hover:bg-[#2A2A2A]"
                    }`}
                  >
                    {page === "footer" ? "Footer & Service Areas" : `${page} Page`}
                  </button>
                ))}
              </div>

              {/* Home Page Editor */}
              {activePage === "home" && siteContent?.home && (
                <div className="space-y-6">
                  {/* Hero Section */}
                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4 flex items-center gap-2">
                      <Layers size={18} className="text-[#7ED4C6]" />
                      Hero Section
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Subtitle</label>
                        <input
                          type="text"
                          value={siteContent.home.hero?.subtitle || ""}
                          onChange={(e) => updatePageContent("home", "hero", "subtitle", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Highlight Text</label>
                        <input
                          type="text"
                          value={siteContent.home.hero?.highlight || ""}
                          onChange={(e) => updatePageContent("home", "hero", "highlight", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#A1A1AA] mb-1">Main Title</label>
                        <input
                          type="text"
                          value={siteContent.home.hero?.title || ""}
                          onChange={(e) => updatePageContent("home", "hero", "title", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#A1A1AA] mb-1">Description</label>
                        <textarea
                          value={siteContent.home.hero?.description || ""}
                          onChange={(e) => updatePageContent("home", "hero", "description", e.target.value)}
                          rows={2}
                          className="form-input w-full"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#A1A1AA] mb-1">Background Image URL</label>
                        <input
                          type="text"
                          value={siteContent.home.hero?.image_url || ""}
                          onChange={(e) => updatePageContent("home", "hero", "image_url", e.target.value)}
                          className="form-input w-full"
                        />
                        {siteContent.home.hero?.image_url && (
                          <img src={siteContent.home.hero.image_url} alt="Hero" className="mt-2 h-20 object-cover" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* About Preview Section */}
                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4 flex items-center gap-2">
                      <Layers size={18} className="text-[#7ED4C6]" />
                      About Preview Section
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Subtitle</label>
                        <input
                          type="text"
                          value={siteContent.home.about_preview?.subtitle || ""}
                          onChange={(e) => updatePageContent("home", "about_preview", "subtitle", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Years Experience</label>
                        <input
                          type="text"
                          value={siteContent.home.about_preview?.years_experience || ""}
                          onChange={(e) => updatePageContent("home", "about_preview", "years_experience", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#A1A1AA] mb-1">Title</label>
                        <input
                          type="text"
                          value={siteContent.home.about_preview?.title || ""}
                          onChange={(e) => updatePageContent("home", "about_preview", "title", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#A1A1AA] mb-1">Description</label>
                        <textarea
                          value={siteContent.home.about_preview?.description || ""}
                          onChange={(e) => updatePageContent("home", "about_preview", "description", e.target.value)}
                          rows={3}
                          className="form-input w-full"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#A1A1AA] mb-1">Image URL</label>
                        <input
                          type="text"
                          value={siteContent.home.about_preview?.image_url || ""}
                          onChange={(e) => updatePageContent("home", "about_preview", "image_url", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Stats Section */}
                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4 flex items-center gap-2">
                      <BarChart3 size={18} className="text-[#7ED4C6]" />
                      Statistics
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {(siteContent.home.stats || []).map((stat, index) => (
                        <div key={index} className="bg-[#0A0A0A] p-3 border border-[#2A2A2A]">
                          <input
                            type="text"
                            value={stat.number}
                            onChange={(e) => {
                              const newStats = [...siteContent.home.stats];
                              newStats[index] = { ...newStats[index], number: e.target.value };
                              setSiteContent(prev => ({
                                ...prev,
                                home: { ...prev.home, stats: newStats }
                              }));
                            }}
                            className="form-input w-full text-center text-xl font-bold mb-2"
                            placeholder="250+"
                          />
                          <input
                            type="text"
                            value={stat.label}
                            onChange={(e) => {
                              const newStats = [...siteContent.home.stats];
                              newStats[index] = { ...newStats[index], label: e.target.value };
                              setSiteContent(prev => ({
                                ...prev,
                                home: { ...prev.home, stats: newStats }
                              }));
                            }}
                            className="form-input w-full text-center text-sm"
                            placeholder="Projects"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Section */}
                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4 flex items-center gap-2">
                      <Layers size={18} className="text-[#7ED4C6]" />
                      Call to Action Section
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">CTA Title</label>
                        <input
                          type="text"
                          value={siteContent.home.cta?.title || ""}
                          onChange={(e) => updatePageContent("home", "cta", "title", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">CTA Description</label>
                        <textarea
                          value={siteContent.home.cta?.description || ""}
                          onChange={(e) => updatePageContent("home", "cta", "description", e.target.value)}
                          rows={2}
                          className="form-input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => savePageContent("home")}
                    disabled={isSaving}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save size={18} />
                    {isSaving ? "Saving..." : "Save Home Page"}
                  </button>
                </div>
              )}

              {/* About Page Editor */}
              {activePage === "about" && siteContent?.about && (
                <div className="space-y-6">
                  {/* Hero */}
                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4">Hero Section</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Subtitle</label>
                        <input
                          type="text"
                          value={siteContent.about.hero?.subtitle || ""}
                          onChange={(e) => updatePageContent("about", "hero", "subtitle", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Title</label>
                        <input
                          type="text"
                          value={siteContent.about.hero?.title || ""}
                          onChange={(e) => updatePageContent("about", "hero", "title", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#A1A1AA] mb-1">Description</label>
                        <textarea
                          value={siteContent.about.hero?.description || ""}
                          onChange={(e) => updatePageContent("about", "hero", "description", e.target.value)}
                          rows={2}
                          className="form-input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Mission */}
                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4">Mission Statement</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Mission Title</label>
                        <input
                          type="text"
                          value={siteContent.about.mission?.title || ""}
                          onChange={(e) => updatePageContent("about", "mission", "title", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Mission Description</label>
                        <textarea
                          value={siteContent.about.mission?.description || ""}
                          onChange={(e) => updatePageContent("about", "mission", "description", e.target.value)}
                          rows={3}
                          className="form-input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Story */}
                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4">Company Story</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Story Title</label>
                        <input
                          type="text"
                          value={siteContent.about.story?.title || ""}
                          onChange={(e) => updatePageContent("about", "story", "title", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      {(siteContent.about.story?.paragraphs || []).map((para, index) => (
                        <div key={index}>
                          <label className="block text-sm text-[#A1A1AA] mb-1">Paragraph {index + 1}</label>
                          <textarea
                            value={para}
                            onChange={(e) => {
                              const newParagraphs = [...siteContent.about.story.paragraphs];
                              newParagraphs[index] = e.target.value;
                              setSiteContent(prev => ({
                                ...prev,
                                about: {
                                  ...prev.about,
                                  story: { ...prev.about.story, paragraphs: newParagraphs }
                                }
                              }));
                            }}
                            rows={2}
                            className="form-input w-full"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => savePageContent("about")}
                    disabled={isSaving}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save size={18} />
                    {isSaving ? "Saving..." : "Save About Page"}
                  </button>
                </div>
              )}

              {/* Contact Page Editor */}
              {activePage === "contact" && siteContent?.contact && (
                <div className="space-y-6">
                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Phone</label>
                        <input
                          type="text"
                          value={siteContent.contact.info?.phone || ""}
                          onChange={(e) => updatePageContent("contact", "info", "phone", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Primary Email</label>
                        <input
                          type="email"
                          value={siteContent.contact.info?.email || ""}
                          onChange={(e) => updatePageContent("contact", "info", "email", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Secondary Email</label>
                        <input
                          type="email"
                          value={siteContent.contact.info?.secondary_email || ""}
                          onChange={(e) => updatePageContent("contact", "info", "secondary_email", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Business Hours</label>
                        <input
                          type="text"
                          value={siteContent.contact.info?.hours || ""}
                          onChange={(e) => updatePageContent("contact", "info", "hours", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#A1A1AA] mb-1">Address / Service Area</label>
                        <input
                          type="text"
                          value={siteContent.contact.info?.address || ""}
                          onChange={(e) => updatePageContent("contact", "info", "address", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4">Page Header</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Title</label>
                        <input
                          type="text"
                          value={siteContent.contact.hero?.title || ""}
                          onChange={(e) => updatePageContent("contact", "hero", "title", e.target.value)}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Description</label>
                        <textarea
                          value={siteContent.contact.hero?.description || ""}
                          onChange={(e) => updatePageContent("contact", "hero", "description", e.target.value)}
                          rows={2}
                          className="form-input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => savePageContent("contact")}
                    disabled={isSaving}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save size={18} />
                    {isSaving ? "Saving..." : "Save Contact Page"}
                  </button>
                </div>
              )}

              {/* Footer Editor */}
              {activePage === "footer" && (
                <div className="space-y-6">
                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4 flex items-center gap-2">
                      <Layers size={18} className="text-[#7ED4C6]" />
                      Footer Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#A1A1AA] mb-1">Company Description</label>
                        <textarea
                          value={siteContent.footer?.company_description || ""}
                          onChange={(e) => setSiteContent(prev => ({
                            ...prev,
                            footer: { ...prev.footer, company_description: e.target.value }
                          }))}
                          rows={2}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Phone</label>
                        <input
                          type="text"
                          value={siteContent.footer?.phone || ""}
                          onChange={(e) => setSiteContent(prev => ({
                            ...prev,
                            footer: { ...prev.footer, phone: e.target.value }
                          }))}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">Email</label>
                        <input
                          type="email"
                          value={siteContent.footer?.email || ""}
                          onChange={(e) => setSiteContent(prev => ({
                            ...prev,
                            footer: { ...prev.footer, email: e.target.value }
                          }))}
                          className="form-input w-full"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-[#A1A1AA] mb-1">Location Text</label>
                        <input
                          type="text"
                          value={siteContent.footer?.location || ""}
                          onChange={(e) => setSiteContent(prev => ({
                            ...prev,
                            footer: { ...prev.footer, location: e.target.value }
                          }))}
                          className="form-input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4 flex items-center gap-2">
                      <Layers size={18} className="text-[#7ED4C6]" />
                      Footer CTA Section
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">CTA Title</label>
                        <input
                          type="text"
                          value={siteContent.footer?.cta_title || ""}
                          onChange={(e) => setSiteContent(prev => ({
                            ...prev,
                            footer: { ...prev.footer, cta_title: e.target.value }
                          }))}
                          className="form-input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-1">CTA Description</label>
                        <textarea
                          value={siteContent.footer?.cta_description || ""}
                          onChange={(e) => setSiteContent(prev => ({
                            ...prev,
                            footer: { ...prev.footer, cta_description: e.target.value }
                          }))}
                          rows={2}
                          className="form-input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#121212] border border-[#2A2A2A] p-6">
                    <h3 className="font-['Oswald'] text-lg text-white mb-4 flex items-center gap-2">
                      <MapPin size={18} className="text-[#7ED4C6]" />
                      Service Areas
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm text-[#A1A1AA] mb-1">State/Region Title</label>
                          <input
                            type="text"
                            value={siteContent.footer?.service_areas?.nj_title || "New Jersey"}
                            onChange={(e) => {
                              setSiteContent(prev => ({
                                ...prev,
                                footer: {
                                  ...prev.footer,
                                  service_areas: {
                                    ...prev.footer?.service_areas,
                                    nj_title: e.target.value
                                  }
                                }
                              }));
                            }}
                            className="form-input w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-[#A1A1AA] mb-1">Areas (one per line)</label>
                          <textarea
                            value={(siteContent.footer?.service_areas?.nj_areas || []).join("\n")}
                            onChange={(e) => {
                              setSiteContent(prev => ({
                                ...prev,
                                footer: {
                                  ...prev.footer,
                                  service_areas: {
                                    ...prev.footer?.service_areas,
                                    nj_areas: e.target.value.split("\n").filter(a => a.trim())
                                  }
                                }
                              }));
                            }}
                            rows={3}
                            className="form-input w-full"
                            placeholder="Newark • Jersey City • Hoboken"
                          />
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm text-[#A1A1AA] mb-1">State/Region Title</label>
                          <input
                            type="text"
                            value={siteContent.footer?.service_areas?.ny_title || "New York"}
                            onChange={(e) => {
                              setSiteContent(prev => ({
                                ...prev,
                                footer: {
                                  ...prev.footer,
                                  service_areas: {
                                    ...prev.footer?.service_areas,
                                    ny_title: e.target.value
                                  }
                                }
                              }));
                            }}
                            className="form-input w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-[#A1A1AA] mb-1">Areas (one per line)</label>
                          <textarea
                            value={(siteContent.footer?.service_areas?.ny_areas || []).join("\n")}
                            onChange={(e) => {
                              setSiteContent(prev => ({
                                ...prev,
                                footer: {
                                  ...prev.footer,
                                  service_areas: {
                                    ...prev.footer?.service_areas,
                                    ny_areas: e.target.value.split("\n").filter(a => a.trim())
                                  }
                                }
                              }));
                            }}
                            rows={3}
                            className="form-input w-full"
                            placeholder="Manhattan • Brooklyn • Queens"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => savePageContent("footer")}
                    disabled={isSaving}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save size={18} />
                    {isSaving ? "Saving..." : "Save Footer & Service Areas"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Services Editor */}
      {contentTab === "services" && (
        <ServicesEditor 
          siteContent={siteContent}
          setSiteContent={setSiteContent}
          token={token}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
        />
      )}

      {/* Projects Management */}
      {contentTab === "projects" && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-['Oswald'] text-xl text-white">Manage Projects</h2>
            <button
              onClick={() => { setSelectedProject(null); setShowProjectModal(true); }}
              className="btn-primary flex items-center gap-2"
              data-testid="add-project-btn"
            >
              <Plus size={18} />
              Add Project
            </button>
          </div>

          {projects.length === 0 ? (
            <div className="bg-[#121212] border border-[#2A2A2A] p-12 text-center">
              <FolderOpen size={48} className="text-[#2A2A2A] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No projects yet. Add your first project!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div key={project.id} className="bg-[#121212] border border-[#2A2A2A] overflow-hidden group">
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                    {project.featured && (
                      <span className="absolute top-2 right-2 bg-[#7ED4C6] text-[#0A0A0A] text-xs px-2 py-1 uppercase">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <span className="text-[#7ED4C6] text-xs uppercase tracking-wider">{project.category}</span>
                    <h3 className="font-['Oswald'] text-lg text-white mt-1">{project.title}</h3>
                    {project.location && (
                      <p className="text-[#A1A1AA] text-sm mt-1">{project.location}</p>
                    )}
                    <p className="text-[#A1A1AA] text-sm mt-2 line-clamp-2">{project.description}</p>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => { setSelectedProject(project); setShowProjectModal(true); }}
                        className="flex-1 bg-[#1E1E1E] text-white px-3 py-2 text-sm flex items-center justify-center gap-2 hover:bg-[#2A2A2A]"
                        data-testid={`edit-project-${project.id}`}
                      >
                        <Edit size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => deleteProject(project.id)}
                        className="bg-red-500/20 text-red-400 px-3 py-2 text-sm flex items-center justify-center gap-2 hover:bg-red-500/30"
                        data-testid={`delete-project-${project.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Testimonials Management */}
      {contentTab === "testimonials" && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-['Oswald'] text-xl text-white">Manage Testimonials</h2>
            <button
              onClick={() => { setSelectedTestimonial(null); setShowTestimonialModal(true); }}
              className="btn-primary flex items-center gap-2"
              data-testid="add-testimonial-btn"
            >
              <Plus size={18} />
              Add Testimonial
            </button>
          </div>

          {testimonials.length === 0 ? (
            <div className="bg-[#121212] border border-[#2A2A2A] p-12 text-center">
              <MessageSquare size={48} className="text-[#2A2A2A] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No testimonials yet. Add your first testimonial!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-[#121212] border border-[#2A2A2A] p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="flex gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={16}
                            className={i < testimonial.rating ? "text-[#7ED4C6] fill-[#7ED4C6]" : "text-[#2A2A2A]"}
                          />
                        ))}
                      </div>
                      <p className="text-white italic mb-4">"{testimonial.content}"</p>
                      <div>
                        <p className="text-white font-semibold">{testimonial.client_name}</p>
                        {testimonial.company && (
                          <p className="text-[#A1A1AA] text-sm">{testimonial.company}</p>
                        )}
                        {testimonial.project_type && (
                          <p className="text-[#7ED4C6] text-xs uppercase mt-1">{testimonial.project_type}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-4 border-t border-[#2A2A2A]">
                    <button
                      onClick={() => { setSelectedTestimonial(testimonial); setShowTestimonialModal(true); }}
                      className="flex-1 bg-[#1E1E1E] text-white px-3 py-2 text-sm flex items-center justify-center gap-2 hover:bg-[#2A2A2A]"
                      data-testid={`edit-testimonial-${testimonial.id}`}
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => deleteTestimonial(testimonial.id)}
                      className="bg-red-500/20 text-red-400 px-3 py-2 text-sm flex items-center justify-center gap-2 hover:bg-red-500/30"
                      data-testid={`delete-testimonial-${testimonial.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Services Editor Component
function ServicesEditor({ siteContent, setSiteContent, token, isSaving, setIsSaving }) {
  const ICON_OPTIONS = ["HardHat", "Building2", "Home", "Paintbrush", "Zap", "ClipboardList", "Hammer", "Wrench", "Truck", "Ruler"];

  const saveServices = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/site-content/services`, siteContent?.services || {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Services saved successfully!");
    } catch (error) {
      toast.error("Failed to save services");
    } finally {
      setIsSaving(false);
    }
  };

  const addService = () => {
    const newService = { title: "New Service", description: "Service description", icon: "HardHat" };
    setSiteContent(prev => ({
      ...prev,
      services: {
        ...prev.services,
        list: [...(prev.services?.list || []), newService]
      }
    }));
  };

  const updateService = (index, field, value) => {
    const newList = [...(siteContent?.services?.list || [])];
    newList[index] = { ...newList[index], [field]: value };
    setSiteContent(prev => ({
      ...prev,
      services: { ...prev.services, list: newList }
    }));
  };

  const removeService = (index) => {
    const newList = (siteContent?.services?.list || []).filter((_, i) => i !== index);
    setSiteContent(prev => ({
      ...prev,
      services: { ...prev.services, list: newList }
    }));
  };

  if (!siteContent?.services) {
    return <div className="text-[#A1A1AA]">Loading services...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-['Oswald'] text-xl text-white">Manage Services</h2>
        <button onClick={addService} className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          Add Service
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(siteContent.services.list || []).map((service, index) => (
          <div key={index} className="bg-[#121212] border border-[#2A2A2A] p-4">
            <div className="flex justify-between items-start mb-3">
              <span className="text-[#7ED4C6] text-xs uppercase">Service {index + 1}</span>
              <button
                onClick={() => removeService(index)}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-[#A1A1AA] mb-1">Title</label>
                <input
                  type="text"
                  value={service.title}
                  onChange={(e) => updateService(index, "title", e.target.value)}
                  className="form-input w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-[#A1A1AA] mb-1">Description</label>
                <textarea
                  value={service.description}
                  onChange={(e) => updateService(index, "description", e.target.value)}
                  rows={2}
                  className="form-input w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-[#A1A1AA] mb-1">Icon</label>
                <select
                  value={service.icon}
                  onChange={(e) => updateService(index, "icon", e.target.value)}
                  className="form-input w-full"
                >
                  {ICON_OPTIONS.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={saveServices}
        disabled={isSaving}
        className="btn-primary flex items-center gap-2"
      >
        <Save size={18} />
        {isSaving ? "Saving..." : "Save Services"}
      </button>
    </div>
  );
}

// Project Modal Component
function ProjectModal({ project, onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    title: project?.title || "",
    category: project?.category || "residential",
    description: project?.description || "",
    image_url: project?.image_url || "",
    gallery_images: project?.gallery_images || [],
    before_image_url: project?.before_image_url || "",
    after_image_url: project?.after_image_url || "",
    location: project?.location || "",
    featured: project?.featured || false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingBefore, setUploadingBefore] = useState(false);
  const [uploadingAfter, setUploadingAfter] = useState(false);

  const handleMainImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setFormData({ ...formData, image_url: response.data.url });
      toast.success("Main image uploaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleGalleryImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingGallery(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        uploadedUrls.push(response.data.url);
      }
      setFormData({ 
        ...formData, 
        gallery_images: [...formData.gallery_images, ...uploadedUrls] 
      });
      toast.success(`${uploadedUrls.length} image(s) added to gallery`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload images");
    } finally {
      setUploadingGallery(false);
    }
  };

  const removeGalleryImage = (index) => {
    const newGallery = formData.gallery_images.filter((_, i) => i !== index);
    setFormData({ ...formData, gallery_images: newGallery });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (project) {
        // Update existing project
        await axios.put(`${API}/admin/projects/${project.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Project updated successfully");
      } else {
        // Create new project
        await axios.post(`${API}/projects`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Project created successfully");
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save project");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-[#121212] border border-[#2A2A2A] w-full max-w-2xl my-8">
        <div className="p-6 border-b border-[#2A2A2A] flex justify-between items-center">
          <h2 className="font-['Oswald'] text-xl text-white">
            {project ? "Edit Project" : "Add New Project"}
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white text-sm mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="form-input w-full"
                placeholder="e.g., Luxury Caribbean Villa"
              />
            </div>

            <div>
              <label className="block text-white text-sm mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
                className="form-input w-full"
              >
                {PROJECT_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-white text-sm mb-2">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="form-input w-full"
              placeholder="e.g., Manhattan, NY"
            />
          </div>

          <div>
            <label className="block text-white text-sm mb-2">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={3}
              className="form-input w-full"
              placeholder="Describe the project..."
            />
          </div>

          {/* Main Image */}
          <div>
            <label className="block text-white text-sm mb-2">Main Image *</label>
            {formData.image_url && (
              <div className="mb-2 relative inline-block">
                <img
                  src={formData.image_url}
                  alt="Main Preview"
                  className="w-40 h-28 object-cover"
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, image_url: "" })}
                  className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full"
                >
                  <X size={12} />
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="form-input flex-1"
                placeholder="Image URL or upload"
              />
              <label className="flex items-center gap-2 cursor-pointer bg-[#1E1E1E] border border-[#2A2A2A] px-4 text-[#A1A1AA] hover:text-white hover:border-[#7ED4C6] transition-colors">
                <Upload size={18} />
                <span className="text-sm">{isUploading ? "..." : "Upload"}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleMainImageUpload}
                  className="hidden"
                  disabled={isUploading}
                />
              </label>
            </div>
          </div>

          {/* Gallery Images */}
          <div>
            <label className="block text-white text-sm mb-2">
              Gallery Images 
              <span className="text-[#A1A1AA] font-normal ml-2">({formData.gallery_images.length} images)</span>
            </label>
            
            {/* Gallery Preview */}
            {formData.gallery_images.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.gallery_images.map((img, index) => (
                  <div key={index} className="relative">
                    <img
                      src={img}
                      alt={`Gallery ${index + 1}`}
                      className="w-20 h-20 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Upload Gallery Images */}
            <label className="flex items-center gap-2 cursor-pointer bg-[#1E1E1E] border border-dashed border-[#2A2A2A] p-4 text-[#A1A1AA] hover:text-white hover:border-[#7ED4C6] transition-colors justify-center">
              <Image size={18} />
              <span>{uploadingGallery ? "Uploading..." : "Add Gallery Images (select multiple)"}</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleGalleryImageUpload}
                className="hidden"
                disabled={uploadingGallery}
              />
            </label>
            <p className="text-[#A1A1AA] text-xs mt-1">Tip: Hold Ctrl/Cmd to select multiple images</p>
          </div>

          {/* Before/After Images */}
          <div className="bg-[#0A0A0A] border border-[#2A2A2A] p-4">
            <h4 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
              <Layers size={16} className="text-[#7ED4C6]" />
              Before & After Comparison (Optional)
            </h4>
            <p className="text-[#A1A1AA] text-xs mb-4">Add before/after images to show project transformation with a comparison slider.</p>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Before Image */}
              <div>
                <label className="block text-xs text-[#A1A1AA] mb-2">Before Image</label>
                {formData.before_image_url ? (
                  <div className="relative mb-2">
                    <img src={formData.before_image_url} alt="Before" className="w-full h-24 object-cover" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, before_image_url: "" })}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X size={10} />
                    </button>
                    <span className="absolute bottom-1 left-1 bg-[#0A0A0A]/80 text-white text-xs px-2 py-0.5">BEFORE</span>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-24 border border-dashed border-[#2A2A2A] cursor-pointer hover:border-[#7ED4C6]">
                    <Upload size={20} className="text-[#A1A1AA] mb-1" />
                    <span className="text-xs text-[#A1A1AA]">{uploadingBefore ? "..." : "Upload"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setUploadingBefore(true);
                        const fd = new FormData();
                        fd.append("file", file);
                        try {
                          const res = await axios.post(`${API}/admin/upload-image`, fd, {
                            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
                          });
                          setFormData({ ...formData, before_image_url: res.data.url });
                        } catch (err) { toast.error("Upload failed"); }
                        setUploadingBefore(false);
                      }}
                      className="hidden"
                      disabled={uploadingBefore}
                    />
                  </label>
                )}
              </div>

              {/* After Image */}
              <div>
                <label className="block text-xs text-[#A1A1AA] mb-2">After Image</label>
                {formData.after_image_url ? (
                  <div className="relative mb-2">
                    <img src={formData.after_image_url} alt="After" className="w-full h-24 object-cover" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, after_image_url: "" })}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X size={10} />
                    </button>
                    <span className="absolute bottom-1 left-1 bg-[#7ED4C6] text-[#0A0A0A] text-xs px-2 py-0.5">AFTER</span>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-24 border border-dashed border-[#2A2A2A] cursor-pointer hover:border-[#7ED4C6]">
                    <Upload size={20} className="text-[#A1A1AA] mb-1" />
                    <span className="text-xs text-[#A1A1AA]">{uploadingAfter ? "..." : "Upload"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setUploadingAfter(true);
                        const fd = new FormData();
                        fd.append("file", file);
                        try {
                          const res = await axios.post(`${API}/admin/upload-image`, fd, {
                            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
                          });
                          setFormData({ ...formData, after_image_url: res.data.url });
                        } catch (err) { toast.error("Upload failed"); }
                        setUploadingAfter(false);
                      }}
                      className="hidden"
                      disabled={uploadingAfter}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="featured"
              checked={formData.featured}
              onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
              className="w-4 h-4"
            />
            <label htmlFor="featured" className="text-white text-sm">Featured Project</label>
          </div>

          <button type="submit" disabled={isLoading || isUploading || uploadingGallery} className="btn-primary w-full">
            {isLoading ? "Saving..." : project ? "Update Project" : "Create Project"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Testimonial Modal Component
function TestimonialModal({ testimonial, onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    client_name: testimonial?.client_name || "",
    company: testimonial?.company || "",
    content: testimonial?.content || "",
    rating: testimonial?.rating || 5,
    project_type: testimonial?.project_type || "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (testimonial) {
        // Update existing testimonial
        await axios.put(`${API}/admin/testimonials/${testimonial.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Testimonial updated successfully");
      } else {
        // Create new testimonial
        await axios.post(`${API}/testimonials`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Testimonial created successfully");
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save testimonial");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#121212] border border-[#2A2A2A] w-full max-w-md">
        <div className="p-6 border-b border-[#2A2A2A] flex justify-between items-center">
          <h2 className="font-['Oswald'] text-xl text-white">
            {testimonial ? "Edit Testimonial" : "Add New Testimonial"}
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-white text-sm mb-2">Client Name *</label>
            <input
              type="text"
              value={formData.client_name}
              onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              required
              className="form-input w-full"
              placeholder="e.g., John Smith"
            />
          </div>

          <div>
            <label className="block text-white text-sm mb-2">Company</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="form-input w-full"
              placeholder="e.g., Smith Enterprises"
            />
          </div>

          <div>
            <label className="block text-white text-sm mb-2">Project Type</label>
            <select
              value={formData.project_type}
              onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
              className="form-input w-full"
            >
              <option value="">Select type</option>
              <option value="Residential">Residential</option>
              <option value="Commercial">Commercial</option>
              <option value="Interior">Interior</option>
            </select>
          </div>

          <div>
            <label className="block text-white text-sm mb-2">Rating</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  className="p-1"
                >
                  <Star
                    size={24}
                    className={star <= formData.rating ? "text-[#7ED4C6] fill-[#7ED4C6]" : "text-[#2A2A2A]"}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-white text-sm mb-2">Testimonial Content *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
              rows={4}
              className="form-input w-full"
              placeholder="What did the client say about your work?"
            />
          </div>

          <button type="submit" disabled={isLoading} className="btn-primary w-full">
            {isLoading ? "Saving..." : testimonial ? "Update Testimonial" : "Create Testimonial"}
          </button>
        </form>
      </div>
    </div>
  );
}
